import {ValidationResultEnum} from "../../../../../types/validation-result.enum";
import {PhoneTypeEnum} from "../../../../dot-rez-api/data-contracts/enums/phone-type.enum";
import {AuthorizedUserModel} from "../authorized-user.model";
import {
    IDotRezPerson,
    IDotRezPersonComment,
    IDotRezPersonDetails,
    IDotRezPersonPhoneNumber
} from "../../../../dot-rez-api/data-contracts/user/person.data-contracts";
import {computed, IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {IUserProfileEditorViewModel} from "../../profile/user-profile-editor-view-model.interface";
import {DialogResult} from "../../../../dialog/dialog-enums";
import {IUserProfileViewModel, PsoTotalPaxOverrideOptions} from "../../profile/user-profile-view-model.interface";
import {PersonModel} from "../../person/person.model";
import {CompanionModel} from "../../companion/companion.model";
import {IPersonViewModel} from "../../person/person-view-model.interface";
import {IDotRezUserSession} from "../../../../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {IPersonTravelDocumentViewModel} from "../../person/person-travel-document-view-model.interface";
import {IPassengerViewModel} from "../../../../booking/models/passenger/passenger-view-model.interface";
import {UserTravelDocumentModel} from "../../travel-documents/user-travel-document.model";
import {PromiseWaiter} from "../../../../../utils/promise-waiter";
import {TimeOfTheDay} from "../../../../time/time.service.interface";
import {
    IBlueBenefitsSubscription
} from "../../../../blue-benefits/models/subscriptions/blue-benefits-subscription.interface";
import {IDotRezUser} from "../../../../dot-rez-api/data-contracts/user/user-data-contracts";
import {IAuthorizedUserProfileDomain} from "./domain/authorized-user-profile-domain.interface";
import {VoidAuthorizedUserProfileDomainModel} from "./domain/void-authorized-user-profile-domain.model";
import {DotRezDomainCodes} from "../../../../dot-rez-api/data-contracts/enums/domain-codes.enum";
import {AgencyAuthorizedUserProfileDomainModel} from "./domain/agency-authorized-user-profile-domain.model";
import {B2cAuthorizedUserProfileDomainModel} from "./domain/b2c-authorized-user-profile-domain.model";
import {Price} from "../../../../currency/price";
import {NullableNumber, NullableString} from "../../../../../types/nullable-types";
import {genderToNumber, genderToString, NullableGender} from "../../../../dot-rez-api/data-contracts/enums/gender.enum";
import {EmailTypeEnum} from "../../../../dot-rez-api/data-contracts/enums/email-type.enum";

export class AuthorizedUserProfileModel extends PersonModel implements IUserProfileViewModel, IUserProfileEditorViewModel {
    constructor(protected readonly user: AuthorizedUserModel) {
        super(user.services);
        makeObservable<this, '_userData' | '_userNameHash' | '_personData' | '_companions' | '_profileDomain'>(this, {
            _userData: observable,
            _userNameHash: observable.ref,
            _personData: observable,
            _companions: observable,
            travelDocuments: computed,
            allBlueBenefitsSubscriptions: observable.ref,
            _profileDomain: observable.ref
        });

        this._profileDomain = new VoidAuthorizedUserProfileDomainModel();

        this._reactions.push(reaction(() => this.services.application.isActive,
            async (isActive) => {
                if (isActive) {
                    await this.loadProfile();
                }
            }, {
                fireImmediately: true
            }));

        this._reactions.push(reaction(() => this.services.configuration.data,
            async () => {
                if (!this.isProfileInitialized) {
                    //This is a recovery fallback in case the configs failed to initialize on app startup
                    await this.loadProfile();
                }
            }));
    }

    private _profileDomain: IAuthorizedUserProfileDomain;
    private _reactions: IReactionDisposer[] = [];
    allBlueBenefitsSubscriptions: IBlueBenefitsSubscription[] = [];

    get allowHoldBooking(): boolean {
        return this._profileDomain.allowHoldBooking;
    }

    get blueBenefitsSubscription(): IBlueBenefitsSubscription {
        const subscription = this.allBlueBenefitsSubscriptions.find(bbs => bbs.isValidSubscription);
        if(subscription) {
            return subscription;
        }

        return this.allBlueBenefitsSubscriptions[0] || this.services.blueBenefits.noSubscription;
    }

    protected get isPhoneNumberRequired(): boolean {
        return true;
    }

    private _companions: CompanionModel[] | null = null;
    get companions(): CompanionModel[] {
        return this._companions || [];
    }

    get isCompanionsListInitialized(): boolean {
        return Boolean(this._companions);
    }

    private _setCompanions(newCompanions: CompanionModel[]): void {
        runInAction(() => {
            this._companions = this._sortCompanions(newCompanions);
        });
    }

    private _userData: IDotRezUser | null = null;
    get userData(): IDotRezUser {
        if(!this._userData) {
            throw new Error('User data was not initialized');
        }
        return this._userData;
    }

    private _personData: IDotRezPerson | null = null;
    get personData(): IDotRezPerson {
        if(!this._personData) {
            throw new Error('Person data was not initialized');
        }
        return this._personData;
    }

    get gender(): NullableGender{
        return genderToString(this.personData.details.gender);
    }

    get formattedDateOfBirth(): NullableString {
        return this.services.time.formatBirthDate(this.dateOfBirth);
    }

    get organizationCredit(): Price | null {
        return this._profileDomain.organizationCredit;
    }

    async getOrganizationCreditForBooking(bookingCurrency: string): Promise<number> {
        await this._profileDomain.updateOrganizationInfo();
        const credit = this.organizationCredit;
        if(!credit || credit.amount <= 0) {
            return 0;
        }

        if(bookingCurrency === credit.currency) {
            return credit.amount;
        }

        const session = await this.getSession();
        const convertedAmount = await session.convertCurrency({
            amount: credit.amount,
            fromCurrencyCode: credit.currency,
            toCurrencyCode: bookingCurrency
        });

        return convertedAmount.convertedAmount;
    }

    get isProfileInitialized(): boolean {
        return Boolean(this._userData) && Boolean(this._personData);
    }

    get userName(): string {
        return this.userData.username;
    }

    get nationalityName(): NullableString {
        if(!this.nationality) return "";

        return this.services.country.tryGetCountry(this.nationality)?.name ?? null;
    }

    get residentCountryName(): NullableString {
        if(!this.residentCountry) return "";

        return this.services.country.tryGetCountry(this.residentCountry)?.name ?? null;
    }

    get phoneNumber(): NullableString {
        return this.homeDotRezPhoneNumber?.number ?? "";
    }

    get workPhoneNumber(): NullableString {
        return this.workDotRezPhoneNumber?.number ?? "";
    }

    private _userNameHash: NullableString = null;
    get userNameHash(): string {
        if(!this._userNameHash) {
            throw new Error('User name hash was not initialized');
        }
        return this._userNameHash;
    }

    get organizationCode(): string {
        return this.userData.codes.organizationCode;
    }

    private _loadUserProfileWaiter: PromiseWaiter = new PromiseWaiter();
    waitForProfileInitialization(): Promise<void> {
        return this._loadUserProfileWaiter.wait();
    }

    dispose(): void {
        this._reactions.forEach(r => r());
        this._reactions = [];
    }

    async loadProfile(): Promise<void> {
        await this._loadUserDetails();

        if(this.isProfileInitialized) {
            if(this.personData.affiliates.length > 0) {
                this._readCompanions().then(companions => this._setCompanions(companions)); // we don't use await here because we don't want to wait until the companions are loaded
            } else {
                this._setCompanions([]);
            }
        }
    }

    async getSession(): Promise<IDotRezUserSession> {
        return await this.user.getSession();
    }

    private async _loadUserDetails(): Promise<void> {
        try {
            const session = await this.getSession();
            const [userData, personData] = await Promise.all([session.getCurrentUser(), session.getCurrentUserPerson()]);
            const userNameHash = await this.services.crypto.computeHash(userData.username);


            runInAction(() => {
                this._userData = userData;
                this._userNameHash = userNameHash;
                this._personData = personData;
                if(userData.codes.domainCode === DotRezDomainCodes.Agency) {
                    this._profileDomain = new AgencyAuthorizedUserProfileDomainModel(this);
                } else {
                    this._profileDomain = new B2cAuthorizedUserProfileDomainModel(this);
                }
            });

            await this._profileDomain.updateOrganizationInfo();

            await this._loadBlueBenefitsSubscriptions(personData.comments);

        } catch (err) {
            this.services.logger.error('Failed to load user profile', err);
            throw err;
        } finally {
            this._loadUserProfileWaiter.resolve();
        }
    }

    async editContactInformation(): Promise<void> {
        const dialogResult = await this._showContactInformationEditor();
        if(dialogResult === DialogResult.Rejected) {
            this.cancelChanges();
        }
    }

    async saveContactInformation(): Promise<ValidationResultEnum> {
        if(!this.hasChanges()) {
            return ValidationResultEnum.Success;
        }

        this.activateErrorsValidation();

        if(this.hasErrors()) {
            return ValidationResultEnum.Failure;
        }

        await this.services.loadingIndicator.execute({
            action: async () => {
                try {
                    await this._saveUserPersonDetails();
                    await this._savePhoneNumbers();
                    await this._saveEmailAddress()
                    this.commitChanges();
                }
                catch(err){

                } finally {
                    await this._loadUserDetails();
                }
            }
        });

        return ValidationResultEnum.Success;
    }

    private async _saveUserPersonDetails(): Promise<void> {
        const personDetails: IDotRezPersonDetails = {
            dateOfBirth: this.services.time.formatBirthDate(this.fields.dateOfBirth.value),
            gender: genderToNumber(this.fields.gender.value!),
            nationality: this.fields.nationality.value!,
            residentCountry: this.fields.residentCountry.value!,
            preferredCurrencyCode: this.services.currency.current,
            preferredCultureCode: this.services.language.currentLocale.code!
        }

        const session = await this.user.getSession();

        await session.updatePersonDetails({
            name: {
                title: this.services.personTitle.personTitleFromGender(this.fields.gender.value),
                first: this.fields.firstName.value!,
                last: this.fields.lastName.value!,
            },

            details: personDetails
        });
    }

    private async _saveEmailAddress(): Promise<void> {
        const session = await this.user.getSession();
        const currentEmailAddress = this.personData.emailAddresses[0];

        if(currentEmailAddress) {
            if(currentEmailAddress.email !== this.fields.emailAddress.value) {
                await session.updateEmailAddress({
                    ...currentEmailAddress,
                    type: EmailTypeEnum.Business,
                    email: this.fields.emailAddress.value  || ''
                });
            }
        } else {
            await session.addEmailAddress({
                default: true,
                type: EmailTypeEnum.Business,
                email: this.fields.emailAddress.value || ''
            });
        }
    }

    private async _savePhoneNumbers(): Promise<void> {
        const session = await this.user.getSession();
        await this._savePhoneNumber(session,
                                    this.workDotRezPhoneNumber,
                                    this.fields.workPhoneNumber.value,
                                    true);
        await this._savePhoneNumber(session,
            this.homeDotRezPhoneNumber,
            this.fields.phoneNumber.value);
    }

    private async _savePhoneNumber(
        session: IDotRezUserSession,
        currentPhoneNumber: IDotRezPersonPhoneNumber | null,
        editNumber: NullableString,
        isWork: boolean = false,
        ) {
        if (currentPhoneNumber) {
            if (currentPhoneNumber.number !== editNumber) {

                await session.updatePhone({
                    ...currentPhoneNumber,
                    number: editNumber || ''
                });
            }
        } else {
            await session.addPhone({
                number: this.fields.workPhoneNumber.value,
                type: isWork ? PhoneTypeEnum.Work :
                                PhoneTypeEnum.Home,
                default: isWork
            });
        }
    }

    getEditor(): IUserProfileEditorViewModel {
        return this;
    }


    getGreetingMessage(): string {
        const language = this.services.language;
        const translationParams = {userName: this._personData?.name.first || ''}

        switch (this.services.time.getTimeOfTheDay()) {
            case TimeOfTheDay.Morning:
                return language.translationFor('Good morning {userName}!').withParams(translationParams);
            case TimeOfTheDay.Evening:
                return language.translationFor('Good evening {userName}!').withParams(translationParams);
            default:
                return language.translationFor('Hi {userName}!').withParams(translationParams);
        }
    }

    async createCompanionFromPassenger(passenger: IPassengerViewModel): Promise<IPersonViewModel> {
        if(!passenger.fields.firstName.value || !passenger.fields.lastName.value) {
            throw new Error('firstName and lastName are required in order to create a companion');
        }
        const userSession = await this.user.getSession();
        const companionData = await this.services.airlineWebapi.addCompanion({
            dotRezToken: userSession.token,
            firstName: passenger.fields.firstName.value,
            lastName: passenger.fields.lastName.value
        });

        const companion = new CompanionModel(this, companionData);
        this._setCompanions( [...this.companions, companion]);
        return companion;
    }

    private _sortCompanions(companions: CompanionModel[]): CompanionModel[] {
        return companions.sort((c1, c2) => c1.getFullName().localeCompare(c2.getFullName()));
    }


    asCompanion(): IPersonViewModel {
        return this;
    }
    protected _createNewTravelDocument(): IPersonTravelDocumentViewModel {
        return new UserTravelDocumentModel(this);
    }

    protected _getPersonDetailsNotCompletedErrorMessage(): string {
        return this.services.language.translate('Please complete your personal details and then you can add a travel document.');
    }


    get companionsCountLimit(): number {
        return this.services.configuration.data.maxAllowedPassengersNumberOnBooking;
    }

    async addCompanion(): Promise<void> {
        if(!this.canAddCompanions) {
            this.services.alert.showError(this.services.language.translate('You cannot add more companions. You have reached you companions limit number'));
            return;
        }

        const companion = new CompanionModel(this);
        await companion.edit();
        if(!companion.isNew) {
            this._setCompanions([...this.companions, companion]);
        }
    }
    
    get travelDocuments(): IPersonTravelDocumentViewModel[] {
        if(!this._personData) {
            return [];
        }
        return this._personData.travelDocuments.map(td => new UserTravelDocumentModel(this, td.personTravelDocumentKey));
    }

    private async _readCompanions(): Promise<CompanionModel[]> {
        return this._profileDomain.readCompanions();
    }
    private async _loadBlueBenefitsSubscriptions(personComments: IDotRezPersonComment[]): Promise<void> {
        const subscriptions = await this._profileDomain.getBlueBenefitsSubscriptions(personComments);
        runInAction(() => {
            this.allBlueBenefitsSubscriptions = subscriptions;
        })
    }
    get canAddCompanions(): boolean {
        return this._profileDomain.canAddCompanions;
    }
    getCompanionsForBooking(): IPersonViewModel[] {
        return this._profileDomain.getCompanionsForBooking();
    }
    private async _showContactInformationEditor(): Promise<DialogResult> {
        return await this._profileDomain.showContactInformationEditor();
    }

    get shouldLoadBookingHistoryOnStartup(): boolean {
        return this._profileDomain.shouldLoadBookingHistoryOnStartup
    }

    getPsoTotalPaxOverride(options: PsoTotalPaxOverrideOptions): NullableNumber {
       return this._profileDomain.getPsoTotalPaxOverride(options);
    }
}
